import { Children } from 'react';
import clsx from 'clsx';
import { Tab } from '@headlessui/react';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';

// Types
import type { TooltipProps } from '@/components/base/Tooltip';

type TabType = {
    label: string;
    icon?: { active: string; inactive: string };
    disabled?: boolean;
    tooltip?: Partial<Omit<TooltipProps, 'children'>>;
};

export type TabsPopoverProps = {
    tabs: TabType[];
    children: React.ReactNode;
    defaultIndex?: number;
    panelsClassName?: string;
    panelClassName?: string;
};

function TabsPopover({
    tabs,
    defaultIndex,
    children,
    panelsClassName,
    panelClassName
}: TabsPopoverProps) {
    return (
        <Tab.Group defaultIndex={defaultIndex}>
            <Tab.List className="scrollbar flex w-full overflow-x-auto overflow-y-hidden border-b border-icoGray-300 text-xs dark:border-icoDarkMode-beton">
                {tabs.map((tab, index) => (
                    <div
                        key={index}
                        className="relative flex-1 border-r border-icoGray-300 last:border-r-0 dark:border-icoDarkMode-beton">
                        <Tooltip {...tab?.tooltip}>
                            <span>
                                <Tab
                                    disabled={tab?.disabled}
                                    data-testid={`tab-${index}`}
                                    className="group flex w-full items-center rounded-none focus:ring-0 focus:ring-offset-0">
                                    {({ selected }) => (
                                        <>
                                            <span
                                                className={clsx(
                                                    'group flex h-full min-h-[36px] w-full items-center justify-center gap-1.5 px-5',
                                                    {
                                                        'text-icoBlue': selected,
                                                        'text-icoGray-400 dark:text-icoDarkMode-wolf':
                                                            !selected,
                                                        'hover:text-icoBlue': !tab.disabled
                                                    }
                                                )}>
                                                {tab.icon ? (
                                                    <>
                                                        <Icon
                                                            className={clsx({
                                                                'hidden group-hover:block group-focus:block':
                                                                    !selected
                                                            })}
                                                            icon={tab.icon.active}
                                                        />
                                                        <Icon
                                                            className={clsx({
                                                                hidden: selected,
                                                                'group-hover:hidden group-focus:hidden':
                                                                    !selected
                                                            })}
                                                            icon={tab.icon.inactive}
                                                        />
                                                    </>
                                                ) : null}
                                                <span>{tab.label}</span>
                                            </span>
                                            <span
                                                className={clsx(
                                                    'absolute bottom-0 left-1/2 h-0.5 w-5 -translate-x-1/2 bg-icoBlue',
                                                    { hidden: !selected }
                                                )}
                                            />
                                        </>
                                    )}
                                </Tab>
                            </span>
                        </Tooltip>
                    </div>
                ))}
            </Tab.List>
            <Tab.Panels className={clsx('w-full', panelsClassName)}>
                {Children.map(children, (panel) => {
                    return (
                        <Tab.Panel
                            className={clsx(
                                'w-full focus:ring-0 focus-visible:outline-none',
                                panelClassName
                            )}>
                            {panel}
                        </Tab.Panel>
                    );
                })}
            </Tab.Panels>
        </Tab.Group>
    );
}

export default TabsPopover;
